import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { FlamingoError } from './flamingo-error.handler';

@Injectable({
    providedIn: 'root'
})
export class FlamingoHttpService {
    private commonHeaders: HttpHeaders;
    private baseUrl = environment.baseUrl + environment.apiBasePath;

    constructor(private http: HttpClient) {
        this.commonHeaders = new HttpHeaders()
            .set('Content-Type', 'application/json; charset=utf-8')
            .set('Accept', 'application/json; charset=utf-8');
    }

    public get(_url: string, params?: HttpParams): Observable<any> {
        const url = this.baseUrl + _url;
        console.log('🔧 HTTP GET ->', url);
        return this.http
            .get(url, {
                headers: this.commonHeaders,
                params: params,
                responseType: 'json',
            })
            .pipe(catchError(this.handleError));
    }

    public getUrl(_url: string, params?: HttpParams): Observable<any> {
        const url = this.baseUrl + _url;
        console.log('🔧 HTTP GET(text) ->', url);
        // For text/URL responses, don't send Accept: application/json header
        // This prevents 406 Not Acceptable errors when server returns text/URL
        // For GET requests, we don't need Content-Type header
        return this.http
            .get(url, {
                params: params,
                responseType: 'text',
            })
            .pipe(catchError(this.handleError));
    }

    public post(_url: string, _body?: any): Observable<any> {
        const url = this.baseUrl + _url;
        console.log('🔧 HTTP POST ->', url);
        let body = _body;

        if (!body) {
            body = {};
        }

        return this.http
            .post(url, body, {
                headers: this.commonHeaders,
                responseType: 'json',
            })
            .pipe(catchError(this.handleError));
    }

    public postWithParams(_url: string, params?: HttpParams, _body?: any): Observable<any> {
        const url = this.baseUrl + _url;
        console.log('🔧 HTTP POST (params) ->', url);
        let body = _body;

        if (!body) {
            body = {};
        }

        return this.http
            .post(url, body, {
                headers: this.commonHeaders,
                params: params,
                responseType: 'json',
            })
            .pipe(catchError(this.handleError));
    }

    public postFormData(_url: string, formData: FormData): Observable<any> {
        const url = this.baseUrl + _url;
        console.log('🔧 HTTP POST(form-data) ->', url);

        return this.http.post(url, formData, {
            responseType: 'json',
        });
    }

    public put(_url: string, _body?: any): Observable<any> {
        const url = this.baseUrl + _url;
        console.log('🔧 HTTP PUT ->', url);
        let body = _body;

        if (!body) {
            body = {};
        }

        return this.http
            .put(url, body, { headers: this.commonHeaders, responseType: 'json' })
            .pipe(catchError(this.handleError));
    }

    public putFormData(_url: string, formData: FormData): Observable<any> {
        const url = this.baseUrl + _url;
        console.log('🔧 HTTP PUT(form-data) ->', url);

        return this.http.put(url, formData, {
            responseType: 'json',
        });
    }

    public delete(_url: string, params?: HttpParams): Observable<any> {
        const url = this.baseUrl + _url;
        console.log('🔧 HTTP DELETE ->', url);
        return this.http
            .delete(url, {
                headers: this.commonHeaders,
                params: params,
                responseType: 'json',
            })
            .pipe(catchError(this.handleError));
    }

    public deleteVoid(_url: string, params?: HttpParams): Observable<void> {
        const url = this.baseUrl + _url;
        console.log('🔧 HTTP DELETE(void) ->', url);
        return this.http
            .delete(url, {
                headers: this.commonHeaders,
                params: params,
                responseType: 'text',
            })
            .pipe(
                map(() => void 0),
                catchError(this.handleError)
            );
    }

    public download(_url: string, params?: HttpParams): Observable<any> {
        const url = this.baseUrl + _url;
        console.log('🔧 HTTP GET(blob) ->', url);
        // For blob/file downloads, don't send Accept: application/json header
        // This prevents 406 Not Acceptable errors when server returns blob/file
        // For GET requests, we don't need Content-Type header
        return this.http
            .get(url, {
                observe: 'response',
                params: params,
                responseType: 'blob',
            })
            .pipe(catchError(this.handleError));
    }

    public openInBrowserFromAWSUrl(_url: string): void {
        let contentType: string | null;
        let contentDisposition: string | null;
        fetch(_url)
            .then(r => {
                contentType = r.headers.get('Content-type');
                contentDisposition = r.headers.get('Content-Disposition');
                return r.blob();
            })
            .then(blob => {
                let fileName: string | null = null;

                // Try to extract filename from Content-Disposition header
                if (contentDisposition) {
                    const match = contentDisposition.match(/"(.*?)"/);
                    if (match && match[1]) {
                        fileName = match[1];
                    }
                }

                // If no filename from header, try to extract from URL
                if (!fileName && _url) {
                    try {
                        const urlObj = new URL(_url);
                        const pathParts = urlObj.pathname.split('/');
                        fileName = pathParts[pathParts.length - 1] || 'document';
                    } catch (e) {
                        fileName = 'document';
                    }
                }

                if (!fileName) {
                    fileName = 'document';
                }

                const filePart = fileName.split('.');
                const fileExtension = filePart.length > 1 ? filePart[filePart.length - 1].toLowerCase() : 'pdf';
                const mimeType = contentType || `application/${fileExtension}`;

                const newBlob = new Blob([blob], { type: mimeType });
                const data = window.URL.createObjectURL(newBlob);

                // If the format is compatible with a browser, open in new tab
                if (fileExtension === 'pdf' || fileExtension === 'xml' || fileExtension === 'json' || fileExtension === 'html') {
                    window.open(data, '_blank');
                    setTimeout(() => window.URL.revokeObjectURL(data), 100);
                    return;
                }

                // Otherwise download as attachment
                const link = document.createElement('a');
                link.href = data;
                link.download = fileName;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Clean up after a delay
                setTimeout(() => window.URL.revokeObjectURL(data), 100);
            })
            .catch(err => {
                console.error('Error downloading from URL:', err);
                // Fallback: try to open URL directly
                window.open(_url, '_blank');
            });
    }

    public downloadAndSaveFile(
        _url: string,
        params?: HttpParams,
        filename?: string,
    ): Observable<void> {
        const url = this.baseUrl + _url;
        console.log('🔧 HTTP GET(blob save) ->', url);
        return this.http
            .get(url, {
                headers: this.commonHeaders,
                observe: 'response',
                params: params,
                responseType: 'blob',
            })
            .pipe(
                map(res => {
                    filename = filename || 'file';
                    const disposition =
                        res.headers.get('content-disposition') || res.headers.get('Content-Disposition');
                    if (disposition && disposition.indexOf('attachment') !== -1) {
                        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                        const matches = filenameRegex.exec(disposition);
                        if (matches != null && matches[1]) {
                            filename = matches[1].replace(/['"]/g, '');
                        }
                    }

                    const obj = window.URL.createObjectURL(new Blob([res.body!], { type: res.body?.type }));
                    const link = window.document.createElement('a');
                    link.download = filename;
                    link.href = obj;
                    link.click();
                }),
                catchError(this.handleError),
            );
    }

    private handleError(error: any): Observable<any> {
        return observableThrowError(new FlamingoError(error));
    }
}
